// chat — the per-subdomain client module for the chat turn flow: a STRICT
// decide/evolve decider (determinism migration M1; the pilot the later
// contexts copy) under the module contract (modules/module.ts). The shell
// mounts `mkChatModule` in the client runtime (app/runtime.ts, S6).
//
// The chat turn flow is the app's highest-async-pressure surface: a turn
// streams live progress, round-trips a multi-second port, can be stopped or
// retried, and can land while the presentation changes underneath it. Before
// M1 its state advanced through ~12 ad-hoc `updateSession`/`setSelected…`
// closures scattered across AppShellHost's async continuations — network
// outcomes writing React state directly, order-dependence invisible and
// untestable. This module concentrates ALL of that into one pure decider so
// every "reply landed vs stop clicked vs stream event, in which order?"
// question becomes a value-level test.
//
// Shape (ADR-1, decisions.jsonl bd36663e): STRICT, mirroring @bilda/core's
// decide/evolve split rather than frame.ts's single fold.
//   - `decideChat(state, command): Result<ChatDecision, ChatError>` — the
//     validating half. A refusal (submit while already drafting, stop while
//     idle) is an EXPLICIT, testable `ChatError`, never a silent no-op. On
//     success it yields a `ChatDecision`: the `ChatEvent[]` that happened,
//     the `ChatEffect[]` the SHELL must interpret (send a turn, interrupt,
//     offer an opening move), and the `Signal[]` other owners handle (toast,
//     world re-read). Both are DATA out of decide — the decider runs neither.
//   - `evolveChat(state, event): ChatState` — the total half. State × Event →
//     State, no Result, no throw; it trusts the payload (invariants belong to
//     decide) and ends in `exhaustSwitchCase`.
//
// Purity (dev-ts, pure-core/impure-shell): no I/O, no clock, no id-minting, no
// React. The shell mints entry/activity/reply ids and reads the clock, then
// hands them in as command payload fields; the decider only folds. Named leaf
// types on a `type` discriminant, summed; distinct command/event per intent
// (never a flag). `ChatSession` (chat/model.ts) is unchanged — `ChatState`
// wraps a `ReadonlyMap` of them plus the `selectedConversationId` absorbed out
// of AppShellHost's `useState` (ADR-6: one owner per fact).
//
// Module topology (C1 hotspot decomposition): this entry file re-exports the
// substrate from the sibling `chat/` folder at the stable import path (the
// sums only; a leaf type is exported from its file only when a sibling
// needs it) —
//  - `chat/state.ts`      — ChatState, initialChatState
//  - `chat/command.ts`    — the ChatCommand sum + its leaf types
//  - `chat/event.ts`      — the ChatEvent sum + its leaf types
//  - `chat/error.ts`      — the ChatError sum + its leaf types
//  - `chat/effect.ts`     — the ChatEffect sum + its leaf types + ChatDecision
//  - `chat/decide.ts`     — decideChat (the validating half)
//  - `chat/evolve.ts`     — evolveChat + evolveChatAll (the total half)
//  - `chat/update.ts`     — updateChat: decide + evolve as the module
//    contract's `Step` (model, effects, Signals) (S3)
//  - `chat/transcript.ts` — transcript/ChatEntry shaping helpers
//  - `chat/historyNav.ts` — the A-11 message-history recall folds
//  - `chat/intents.ts`    — mkChatIntents: UI gestures → chat commands (S6)
//  - `chat/view.tsx`      — ChatFullView + nascentComposer: the React
//    projection (S7)
//  - `chat/run.ts`        — mkRunChat: the interpreter, the module's ONE
//    impure file (S5)
//  - `chat/model.ts`      — ChatSession (moved from app/chatSession.ts in S1;
//    imported directly, not re-exported here)

import type { ChatCommand } from "./chat/command.js";
import type { ChatEffect } from "./chat/effect.js";
import type { ChatError } from "./chat/error.js";
import { type ChatDeps, mkRunChat } from "./chat/run.js";
import { type ChatState, initialChatState } from "./chat/state.js";
import { inboundChat, updateChat } from "./chat/update.js";
import type { Module } from "./module.js";

export type { ChatCommand } from "./chat/command.js";
export { decideChat } from "./chat/decide.js";
export type { ChatDecision, ChatEffect } from "./chat/effect.js";
export type { ChatError } from "./chat/error.js";
export type { ChatEvent } from "./chat/event.js";
export { evolveChat, evolveChatAll } from "./chat/evolve.js";
export type { ChatIntents } from "./chat/intents.js";
export { mkChatIntents } from "./chat/intents.js";
export type { TurnFailure } from "./chat/model.js";
export type { ChatDeps } from "./chat/run.js";
export type { ChatState } from "./chat/state.js";
export { initialChatState } from "./chat/state.js";
export type { ChatStep } from "./chat/update.js";
export { updateChat } from "./chat/update.js";
export { ChatFullView, nascentComposer } from "./chat/view.js";

export type ChatModule = Module<ChatState, ChatCommand, ChatEffect, ChatError>;

// The module, its interpreter bound to the ports (built once at the mount).
export const mkChatModule = (deps: ChatDeps): ChatModule => ({
    init: initialChatState,
    update: updateChat,
    run: mkRunChat(deps),
    inbound: inboundChat,
});
